import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import PageTransition from '../components/ui/PageTransition';
import SectionHeading from '../components/ui/SectionHeading';
import Button from '../components/ui/Button';
import HeroSection from '../components/sections/HeroSection';
import CtaSection from '../components/sections/CtaSection';

const AboutPage = () => {
  return (
    <PageTransition>
      <Helmet>
        <title>About Consiliuma | British Web Design & Development Experts</title>
        <meta name="description" content="Discover our story of delivering exceptional web design and development from Nottingham. British expertise with global reach, serving clients worldwide from our UK, USA, and SE Asia offices." />
        <meta name="keywords" content="Web Design Company Nottingham, British Web Developers, Web Agency Nottingham, International Web Design" />
        <link rel="canonical" href="/about" />

        {/* Open Graph Tags */}
        <meta property="og:title" content="About Consiliuma | British Web Design & Development Experts" />
        <meta property="og:description" content="Discover our story of delivering exceptional web design and development from Nottingham. British expertise with global reach, serving clients worldwide from our UK, USA, and SE Asia offices." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://consiliuma.co.uk/about" />
        <meta property="og:image" content="https://images.pexels.com/photos/3184292/pexels-photo-3184292.jpeg" />
        <meta property="og:site_name" content="Consiliuma" />
        <meta property="og:locale" content="en_GB" />

        {/* Schema.org data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "AboutPage",
            "name": "About Consiliuma | British Web Design & Development Experts",
            "description": "Discover our story of delivering exceptional web design and development from Nottingham.",
            "url": "https://consiliuma.co.uk/about",
            "mainEntity": {
              "@type": "Organization",
              "name": "Consiliuma",
              "description": "Premium web design and development company based in Nottingham",
              "foundingDate": "2020",
              "areaServed": ["United Kingdom", "United States", "Southeast Asia"],
              "address": {
                "@type": "PostalAddress",
                "addressLocality": "Nottingham",
                "addressCountry": "GB"
              }
            }
          })}
        </script>
      </Helmet>

      <main>
        <h1 className="sr-only">About Consiliuma - British Web Design & Development Experts</h1>

        <HeroSection
          title="About Consiliuma"
          subtitle="British expertise delivering exceptional digital experiences worldwide"
          ctaText="Get in Touch"
          ctaLink="/contact"
          backgroundImage="https://images.pexels.com/photos/3184292/pexels-photo-3184292.jpeg"
        />

        {/* Our Story */}
        <section className="py-16 md:py-24" aria-labelledby="story-heading">
          <div className="container-custom">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
              >
                <h2 id="story-heading" className="text-3xl md:text-4xl font-bold text-primary mb-6">Our Story</h2>
                <p className="text-xl text-secondary mb-8">British expertise with global reach</p>
                <p className="mb-6">
                  Founded in Nottingham, Consiliuma began with a simple mission: to deliver exceptional web design and development services that combine British quality standards with global perspective.
                </p>
                <p className="mb-6">
                  Our journey started with a team of passionate developers who believed in creating websites that not only look beautiful but also drive real business results. As our reputation grew, so did our team and our reach.
                </p>
                <p className="mb-6">
                  Today, with offices in Nottingham, Arizona, and Southeast Asia, we serve clients around the world while maintaining our core values of technical excellence, creative innovation, and exceptional service.
                </p>
                <p className="mb-6">
                  Our British team brings decades of combined experience in full-stack development, with specialties ranging from e-commerce solutions to complex web applications and content management systems.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
              >
                <img 
                  src="https://images.pexels.com/photos/3182826/pexels-photo-3182826.jpeg" 
                  alt="Consiliuma team collaboration" 
                  className="rounded-lg shadow-lg w-full h-auto"
                />
              </motion.div>
            </div>
          </div>
        </section>

        {/* Our Mission */}
        <section className="py-16 md:py-24 bg-background-alt" aria-labelledby="mission-heading">
          <div className="container-custom">
            <h2 id="mission-heading" className="text-3xl md:text-4xl font-bold text-primary mb-6 text-center">Our Mission & Values</h2>
            <p className="text-xl text-secondary mb-12 text-center">What drives us every day</p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
              <motion.div 
                className="bg-white p-6 rounded-lg shadow-md border-t-4 border-primary"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
              >
                <h3 className="text-xl font-bold mb-4 text-primary">Our Mission</h3>
                <p className="text-secondary">
                  To empower businesses with web solutions that drive growth, enhance brand presence, and deliver exceptional user experiences. We combine technical excellence with creative innovation to create digital assets that provide lasting value.
                </p>
              </motion.div>

              <motion.div 
                className="bg-white p-6 rounded-lg shadow-md border-t-4 border-primary"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                viewport={{ once: true }}
              >
                <h3 className="text-xl font-bold mb-4 text-primary">Our Vision</h3>
                <p className="text-secondary">
                  To be the leading provider of premium web development services, recognized globally for our British quality standards, innovative solutions, and exceptional client relationships. We aim to set new benchmarks for excellence in digital experiences.
                </p>
              </motion.div>

              <motion.div 
                className="bg-white p-6 rounded-lg shadow-md border-t-4 border-primary"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
                viewport={{ once: true }}
              >
                <h3 className="text-xl font-bold mb-4 text-primary">Our Values</h3>
                <ul className="text-secondary space-y-2">
                  <li>• Excellence in everything we deliver</li>
                  <li>• Integrity in all client relationships</li>
                  <li>• Innovation in technical solutions</li>
                  <li>• Accessibility and inclusivity by design</li>
                  <li>• Continuous learning and improvement</li>
                </ul>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Global Presence */}
        <section className="py-16 md:py-24" aria-labelledby="presence-heading">
          <div className="container-custom">
            <h2 id="presence-heading" className="text-3xl md:text-4xl font-bold text-primary mb-6 text-center">Our Global Presence</h2>
            <p className="text-xl text-secondary mb-12 text-center">Serving clients worldwide from strategic locations</p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
              <motion.div 
                className="bg-white p-6 rounded-lg shadow-md text-center"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
              >
                <img 
                  src="https://images.pexels.com/photos/77171/pexels-photo-77171.jpeg" 
                  alt="Nottingham, UK" 
                  className="w-full h-48 object-cover rounded-lg mb-4"
                />
                <h3 className="text-xl font-bold mb-2 text-primary">Nottingham, UK</h3>
                <p className="text-secondary">
                  Our headquarters and primary development center, where our core British team creates innovative web solutions for clients across Europe and beyond.
                </p>
              </motion.div>

              <motion.div 
                className="bg-white p-6 rounded-lg shadow-md text-center"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                viewport={{ once: true }}
              >
                <img 
                  src="https://images.pexels.com/photos/2317408/pexels-photo-2317408.jpeg" 
                  alt="Arizona, USA" 
                  className="w-full h-48 object-cover rounded-lg mb-4"
                />
                <h3 className="text-xl font-bold mb-2 text-primary">Arizona, USA</h3>
                <p className="text-secondary">
                  Our North American hub, serving clients across the United States with the same British quality standards and expertise that defines Consiliuma.
                </p>
              </motion.div>

              <motion.div 
                className="bg-white p-6 rounded-lg shadow-md text-center"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
                viewport={{ once: true }}
              >
                <img 
                  src="https://images.pexels.com/photos/466685/pexels-photo-466685.jpeg" 
                  alt="Southeast Asia" 
                  className="w-full h-48 object-cover rounded-lg mb-4"
                />
                <h3 className="text-xl font-bold mb-2 text-primary">Southeast Asia</h3>
                <p className="text-secondary">
                  Our Asia-Pacific office enables us to provide round-the-clock service and local expertise for clients in Asia, Australia, and the Pacific region.
                </p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-16 md:py-24 bg-secondary text-white" aria-labelledby="why-choose-heading">
          <div className="container-custom">
            <h2 id="why-choose-heading" className="text-3xl md:text-4xl font-bold text-white mb-6 text-center">Why Choose Consiliuma</h2>
            <p className="text-xl text-white/90 mb-12 text-center">British expertise meeting global standards</p>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mt-12">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                viewport={{ once: true }}
              >
                <h3 className="text-xl font-bold mb-3 text-primary-light">British Expertise</h3>
                <p className="text-gray-300">
                  Our all-British development team brings exceptional quality standards and technical expertise to every project.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.1 }}
                viewport={{ once: true }}
              >
                <h3 className="text-xl font-bold mb-3 text-primary-light">Global Reach</h3>
                <p className="text-gray-300">
                  With offices in three continents, we understand diverse markets and can support clients anywhere in the world.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.2 }}
                viewport={{ once: true }}
              >
                <h3 className="text-xl font-bold mb-3 text-primary-light">Full-Stack Capabilities</h3>
                <p className="text-gray-300">
                  From front-end design to back-end development, we handle all aspects of your web project under one roof.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.3 }}
                viewport={{ once: true }}
              >
                <h3 className="text-xl font-bold mb-3 text-primary-light">Ongoing Support</h3>
                <p className="text-gray-300">
                  Our relationship doesn't end at launch. We provide comprehensive maintenance and support for lasting success.
                </p>
              </motion.div>
            </div>

            <div className="text-center mt-12">
              <Button to="/why-choose-us" variant="outline" className="border-white text-white hover:bg-white hover:text-secondary">
                Learn More About Our Approach
              </Button>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <CtaSection
          title="Ready to Start Your Project?"
          subtitle="Let's discuss how our British expertise can help your business succeed online."
          buttonText="Get in Touch"
          buttonLink="/contact"
        />
      </main>
    </PageTransition>
  );
};

export default AboutPage;